<template>
  <div ref="stack" class="platform-banners">
    <SeatsExceededBanner/>
    <PlatformAlertBanner/>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import SeatsExceededBanner from '@/views/RenovaDashboard/CommonParts/SeatsExceededBanner';
import PlatformAlertBanner from '@/views/RenovaDashboard/CommonParts/PlatformAlertBanner';

/**
 * The strip of platform-wide banners pinned above everything else.
 *
 * This exists to own two things the layout used to hardcode for a single banner: the fixed
 * positioning, and how far the page below has to be pushed down. That offset was
 * `padding-top: 48px` guarded by the seats-exceeded condition duplicated onto the layout root —
 * correct for exactly one banner of exactly one height, and wrong the moment a second appeared or
 * the first wrapped to two lines on a narrow window.
 *
 * So the height is measured rather than assumed, and reported upward. Each child keeps its own
 * `v-if`, so an empty stack measures zero and the layout is unchanged.
 */
export default {
  name: 'PlatformBanners',
  components: { SeatsExceededBanner, PlatformAlertBanner },
  data() {
    return { observer: null };
  },
  computed: {
    ...mapGetters(['platformAlerts']),
  },
  watch: {
    /**
     * Re-measure when the alert list changes.
     *
     * Belt and braces for the `updated()` hook below: a child re-rendering off its own Vuex
     * dependency does not necessarily run the PARENT's updated hook, so on a browser with no
     * ResizeObserver an alert arriving later would leave the emitted height at 0 and the strip
     * would sit on top of the navbar.
     */
    platformAlerts() {
      this.$nextTick(this.emitHeight);
    },
  },
  mounted() {
    this.emitHeight();
    // ResizeObserver rather than a window resize listener: the stack also changes height when an
    // alert arrives over the websocket, which fires no resize event.
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(() => this.emitHeight());
      this.observer.observe(this.$refs.stack);
    }
  },
  updated() {
    this.emitHeight();
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.$emit('height', 0);
  },
  methods: {
    emitHeight() {
      const el = this.$refs.stack;
      this.$emit('height', el ? Math.round(el.getBoundingClientRect().height) : 0);
    },
  },
};
</script>

<style lang="scss" scoped>
.platform-banners {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}
</style>
